import styled, { css } from 'styled-components';
import { ellipsis } from 'polished';
import { TableLayout } from './types';

export const TableHeaderCellWrapper = styled.th<{ active?: boolean }>`
  ${({ onClick, active, theme }) =>
    !!onClick
      ? css`
          cursor: pointer;

          ${!active &&
          css`
            &:hover {
              & > div {
                color: ${theme.mosaiq.colors.passive};
              }
            }
          `}
        `
      : css`
          cursor: default;
        `}

  & > div {
    ${ellipsis()}
    display: inline-flex;
    align-items: center;
    ${({ theme }) => theme.mosaiq.typography.body4({ singleLine: true })}

    ${({ theme, active }) =>
      active
        ? css`
            color: ${theme.mosaiq.colors.body};

            svg {
              color: ${({ theme }) => theme.mosaiq.colors.body};
            }
          `
        : css`
            color: ${theme.mosaiq.colors.placeholder};
          `}
  }
`;

export const TableHeader = styled.thead`
  th {
    text-align: left;
    white-space: pre;
    padding-bottom: ${({ theme }) => theme.spacing(1)}px;
    padding-left: ${({ theme }) => theme.spacing(1)}px;
    vertical-align: middle;
  }
`;

export const TableRow = styled.tr<{ isSelected?: boolean }>`
  position: relative;

  ${({ onClick }) =>
    !!onClick
      ? css`
          cursor: pointer;
        `
      : css`
          cursor: default;
        `}

  &:last-of-type {
    border-bottom: 0 none;
    box-shadow: none;

    td {
      border-bottom: thin solid ${({ theme }) => theme.mosaiq.colors.grey40};
    }
  }

  td {
    border-top: thin solid ${({ theme }) => theme.mosaiq.colors.grey40};
    padding: ${({ theme }) => theme.spacing(1.5)}px;
    vertical-align: middle;
  }

  td.showOnHover > * {
    opacity: 0;
    pointer-events: none;
    transition: 0.2s ease;
  }

  &:hover,
  &.dragging {
    td {
      border-top: thin solid ${({ theme }) => theme.mosaiq.colors.white};
      background-color: ${({ theme }) => theme.mosaiq.colors.grey};

      &:only-of-type {
        border-radius: ${({ theme }) => theme.spacing(1.5)}px;
      }
      &:not(:only-of-type):first-of-type {
        border-radius: ${({ theme }) => `${theme.spacing(1.5)}px 0px 0px ${theme.spacing(1.5)}px`};
      }
      &:not(:only-of-type):last-of-type {
        border-radius: ${({ theme }) => `0px ${theme.spacing(1.5)}px ${theme.spacing(1.5)}px 0px`};
      }
    }

    td.showOnHover > * {
      opacity: 1;
      pointer-events: all;
    }

    + tr {
      td {
        border-top: thin solid ${({ theme }) => theme.mosaiq.colors.white};
      }
    }

    &:last-of-type {
      td {
        border-bottom: thin solid ${({ theme }) => theme.mosaiq.colors.white};
      }
    }
  }

  &.dragging {
    box-shadow: ${({ theme }) => theme.mosaiq.elevation[1]};
    border-radius: ${({ theme }) => theme.spacing(1.5)}px;
  }

  ${({ isSelected }) =>
    !!isSelected &&
    css`
      td {
        border-top: thin solid ${({ theme }) => theme.mosaiq.colors.white};
        background-color: ${({ theme }) => theme.mosaiq.colors.sandyHeath};
        &:not(:only-of-type):nth-of-type(2) {
          border-radius: ${({ theme }) =>
            `${theme.spacing(1.5)}px 0px 0px ${theme.spacing(1.5)}px`};
        }
        &:not(:only-of-type):last-of-type {
          border-radius: ${({ theme }) =>
            `0px ${theme.spacing(1.5)}px ${theme.spacing(1.5)}px 0px`};
        }
      }

      td.showOnHover > * {
        opacity: 1;
        pointer-events: all;
      }

      + tr {
        td {
          border-top: thin solid ${({ theme }) => theme.mosaiq.colors.white};
        }
      }

      &:last-of-type {
        td {
          border-bottom: thin solid ${({ theme }) => theme.mosaiq.colors.white};
        }
      }
    `}
`;

export const TableContainer = styled.table<{
  disabled: boolean;
  transparentBg?: boolean;
  tableLayout?: TableLayout;
  selectTable?: boolean;
  hasSelection?: boolean;
}>`
  ${({ theme, transparentBg }) =>
    transparentBg
      ? css`
          background-color: transparent;

          ${TableRow} {
            &:hover {
              td {
                border-top: thin solid ${theme.mosaiq.colors.grey40};
                background-color: transparent;
              }

              + tr {
                td {
                  border-top: thin solid ${theme.mosaiq.colors.grey40};
                }
              }

              &:last-of-type {
                td {
                  border-bottom: thin solid ${theme.mosaiq.colors.grey40};
                }
              }
            }
          }
        `
      : css`
          background-color: ${theme.mosaiq.colors.white};
        `}

  min-width: 100%;
  max-width: 100%;
  border-collapse: separate;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;

      ${TableRow} {
        cursor: default;
        pointer-events: none;
      }
    `}

  ${({ tableLayout }) =>
    tableLayout &&
    css`
      table-layout: ${tableLayout};
    `}

  ${({ selectTable }) =>
    selectTable &&
    css`
      ${TableRow}, ${TableHeader} {
        &:hover {
          td {
            &:not(:only-of-type):nth-of-type(2) {
              border-radius: ${({ theme }) =>
                `${theme.spacing(1.5)}px 0px 0px ${theme.spacing(1.5)}px`};
            }
            &:not(:only-of-type):last-of-type {
              border-radius: ${({ theme }) =>
                `0px ${theme.spacing(1.5)}px ${theme.spacing(1.5)}px 0px`};
            }
          }
        }
        th:first-child {
          padding-bottom: ${({ theme }) => theme.spacing(0.25)}px;
        }
        td:first-child,
        th:first-child {
          padding-right: ${({ theme }) => theme.spacing(0.75)}px;
          border: none;
          background: none;
          & > * {
            opacity: 0;
            pointer-events: none;
          }
        }
        &:hover {
          td:first-child > *,
          th:first-child > * {
            opacity: 1;
            pointer-events: all;
          }
        }
      }
    `}

    ${({ hasSelection }) =>
    hasSelection &&
    css`
      ${TableRow}, ${TableHeader} {
        td:first-child > *,
        th:first-child > * {
          opacity: 1;
          pointer-events: all;
        }
      }
    `}

  &.sticky {
    overflow: scroll;
    .header,
    .footer {
      position: sticky;
      z-index: 1;
      width: fit-content;
    }

    .header {
      top: 0;
      box-shadow: 0px 3px 3px ${({ theme }) => theme.mosaiq.colors.passive};
    }

    .footer {
      bottom: 0;
      box-shadow: 0px -3px 3px ${({ theme }) => theme.mosaiq.colors.passive};
    }

    .body {
      position: relative;
      z-index: 0;
    }

    [data-sticky-td] {
      position: sticky;
    }
  }
`;

export const TableWrapper = styled.div<{ selectTable?: boolean }>`
  position: relative;
  overflow-x: auto;
  ${({ selectTable }) =>
    selectTable &&
    css`
      margin-left: ${({ theme }) => -theme.spacing(4.375)}px;
    `}
`;
